import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, PartyPopper, ShoppingBag, FileText, Download } from "lucide-react";
import { QUESTIONS, AGE_RANGE_OPTIONS } from "@/lib/iqQuestions";
import { IQProgressBar } from "./IQProgressBar";
import { SAFE_BAND_URL } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = QUESTIONS.length + 1; // +1 for the contact step
const CLINICAL_PILOT_PDF = "/docs/WearSpray_Clinical_Pilot_Overview.pdf";
const EVAL_INTERESTED = ["Yes, I would like to participate if eligible", "Maybe, please send me more information"];

function fireEvent(name, params = {}) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, params);
  }
}

const emptyAnswers = {
  products: [], products_other: "",
  usage_contexts: [], usage_other: "",
  frustrations: [], frustrations_other: "",
  features: [],
  value_perception: "",
  devices: [], devices_other: "",
  open_feedback: "",
  eval_interest: "",
};

export function IQQuestionnaire({ source = "direct" }) {
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0); // 0..QUESTIONS.length-1, then "contact"
  const [answers, setAnswers] = useState(emptyAnswers);
  const [contact, setContact] = useState({
    first_name: "", email: "", country: "", age_range: "",
    consent_updates: false, consent_research: false, website: "",
  });
  const [contactError, setContactError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error

  const start = () => {
    setStarted(true);
    fireEvent("iq_questionnaire_start");
  };

  const toggleMulti = (field, option) => {
    setAnswers((a) => {
      const list = a[field].includes(option) ? a[field].filter((o) => o !== option) : [...a[field], option];
      return { ...a, [field]: list };
    });
  };

  const setSingle = (field, option) => setAnswers((a) => ({ ...a, [field]: option }));
  const setText = (field, val) => setAnswers((a) => ({ ...a, [field]: val }));

  const goNext = () => setStepIndex((i) => i + 1);
  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));

  const submit = async () => {
    if (!contact.first_name.trim() || !contact.email.trim()) {
      setContactError("Please share your first name and email to join.");
      return;
    }
    setContactError("");
    setStatus("submitting");
    try {
      await axios.post(`${API}/iq/register`, { ...answers, ...contact, source });
      fireEvent("iq_questionnaire_complete");
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="iq-confirmation" className="text-center py-10 max-w-lg mx-auto">
        <PartyPopper size={40} className="text-[#ff2d78] mx-auto" strokeWidth={1.5} />
        <h2 className="font-heading text-2xl sm:text-3xl font-medium text-black mt-6 tracking-tight">
          Welcome to the Future of Personalized Skincare
        </h2>
        <p className="font-body text-zinc-600 mt-4 leading-relaxed">
          Thank you for sharing your interests with WearSpray IQ. Your feedback will help us
          understand how personalized skincare intelligence and convenient product access could
          fit into everyday life. We look forward to keeping you informed, according to your
          communication preferences.
        </p>
        {EVAL_INTERESTED.includes(answers.eval_interest) && (
          <a
            href={CLINICAL_PILOT_PDF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="iq-confirmation-clinical-pilot-download"
            className="group flex items-center gap-4 text-left bg-zinc-50 border border-zinc-200 rounded-2xl p-5 mt-7 hover:border-[#ff2d78] transition-colors"
          >
            <div className="w-11 h-11 rounded-xl bg-[#ff2d78]/10 flex items-center justify-center text-[#ff2d78] shrink-0">
              <FileText size={20} strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <p className="font-body text-sm font-semibold text-black">What to Expect: Our Early Clinical Pilot Concept</p>
              <p className="font-body text-xs text-zinc-500 mt-0.5">A short, plain-language overview of our early research planning. Subject to change.</p>
            </div>
            <Download size={16} className="text-zinc-400 group-hover:text-[#ff2d78] transition-colors shrink-0" />
          </a>
        )}
        <Link
          to="/"
          data-testid="iq-explore-wearspray"
          className="inline-flex items-center gap-2 font-body text-sm font-medium bg-black text-white px-7 py-3.5 mt-8 hover:bg-[#ff2d78] transition-colors"
        >
          Explore WearSpray <ArrowRight size={15} />
        </Link>
        <div className="mt-5">
          <a
            href={SAFE_BAND_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="iq-confirmation-shop-safeband"
            className="inline-flex items-center gap-1.5 font-body text-sm text-zinc-500 hover:text-[#ff2d78] transition-colors"
          >
            <ShoppingBag size={14} /> Can't wait? Shop the real wearable at SafeBandPro.com
          </a>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <button
        data-testid="iq-start-button"
        onClick={start}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-body text-sm sm:text-base font-semibold bg-[#ff2d78] text-white px-8 py-4 sm:py-5 rounded-full hover:bg-black transition-colors shadow-[0_8px_30px_-8px_rgba(255,45,120,0.5)]"
      >
        Join the WearSpray IQ Community <ArrowRight size={17} />
      </button>
    );
  }

  const isContactStep = stepIndex === QUESTIONS.length;
  const q = !isContactStep ? QUESTIONS[stepIndex] : null;

  return (
    <div data-testid="iq-questionnaire" className="max-w-xl mx-auto bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]">
      <IQProgressBar step={stepIndex + 1} total={TOTAL_STEPS} />

      <AnimatePresence mode="wait">
        <motion.div
          key={stepIndex}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25 }}
          className="mt-7"
        >
          {!isContactStep && q.type !== "text" && (
            <QuestionChoices q={q} answers={answers} toggleMulti={toggleMulti} setSingle={setSingle} setText={setText} />
          )}
          {!isContactStep && q.type === "text" && (
            <TextQuestion q={q} answers={answers} setText={setText} />
          )}
          {isContactStep && (
            <ContactStep contact={contact} setContact={setContact} error={contactError} />
          )}
        </motion.div>
      </AnimatePresence>

      {status === "error" && (
        <p data-testid="iq-submit-error" className="font-body text-sm text-red-600 mt-5">
          Something went wrong submitting your answers. Your responses are still here — please try again.
        </p>
      )}

      <div className="flex items-center justify-between mt-8 gap-4">
        <button
          data-testid="iq-back-button"
          onClick={goBack}
          disabled={stepIndex === 0}
          className="inline-flex items-center gap-1.5 font-body text-sm text-zinc-500 disabled:opacity-0 hover:text-black transition-colors"
        >
          <ArrowLeft size={15} /> Back
        </button>
        {!isContactStep ? (
          <button
            data-testid="iq-next-button"
            onClick={goNext}
            className="inline-flex items-center gap-2 font-body text-sm font-semibold bg-black text-white px-7 py-3.5 rounded-full hover:bg-[#ff2d78] transition-colors"
          >
            Next <ArrowRight size={15} />
          </button>
        ) : (
          <button
            data-testid="iq-submit-button"
            onClick={submit}
            disabled={status === "submitting"}
            className="inline-flex items-center gap-2 font-body text-sm font-semibold bg-[#ff2d78] text-white px-7 py-3.5 rounded-full hover:bg-black transition-colors disabled:opacity-60"
          >
            {status === "submitting" ? (
              <><Loader2 size={15} className="animate-spin" /> Submitting…</>
            ) : (
              <><CheckCircle2 size={15} /> Submit</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function QuestionChoices({ q, answers, toggleMulti, setSingle, setText }) {
  const selected = answers[q.field];
  const isOtherSelected = q.otherField && (q.type === "multi" ? selected.includes("Other") : selected === "Other");
  return (
    <div>
      <h3 className="font-heading text-lg sm:text-xl font-medium text-black leading-snug">{q.title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-5">
        {q.options.map((opt) => {
          const active = q.type === "multi" ? selected.includes(opt) : selected === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`iq-option-${q.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => (q.type === "multi" ? toggleMulti(q.field, opt) : setSingle(q.field, opt))}
              className={`text-left font-body text-sm px-4 py-3.5 rounded-xl border transition-colors ${
                active
                  ? "border-[#ff2d78] bg-[#ff2d78]/[0.06] text-black font-medium"
                  : "border-zinc-200 text-zinc-700 hover:border-zinc-400"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {isOtherSelected && (
        <input
          data-testid={`iq-other-${q.key}`}
          value={answers[q.otherField]}
          onChange={(e) => setText(q.otherField, e.target.value)}
          placeholder="Tell us more…"
          className="w-full border border-zinc-300 rounded-xl px-4 py-3 mt-3 text-sm text-black focus:outline-none focus:border-[#ff2d78] transition-colors"
        />
      )}
    </div>
  );
}

function TextQuestion({ q, answers, setText }) {
  return (
    <div>
      <h3 className="font-heading text-lg sm:text-xl font-medium text-black leading-snug">{q.title}</h3>
      {q.optional && <p className="font-mono text-[11px] tracking-[0.1em] uppercase text-zinc-400 mt-1.5">Optional</p>}
      <textarea
        data-testid={`iq-text-${q.key}`}
        rows={4}
        value={answers[q.field]}
        onChange={(e) => setText(q.field, e.target.value)}
        placeholder="Share your thoughts…"
        className="w-full border border-zinc-300 rounded-xl px-4 py-3.5 mt-5 text-sm text-black resize-none focus:outline-none focus:border-[#ff2d78] transition-colors"
      />
    </div>
  );
}

function ContactStep({ contact, setContact, error }) {
  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  const toggle = (k) => () => setContact((c) => ({ ...c, [k]: !c[k] }));
  return (
    <div>
      <h3 className="font-heading text-lg sm:text-xl font-medium text-black leading-snug">Almost there — how can we reach you?</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-5">
        <input
          data-testid="iq-contact-first-name"
          required
          value={contact.first_name}
          onChange={set("first_name")}
          placeholder="First name *"
          className="w-full border border-zinc-300 rounded-xl px-4 py-3.5 text-sm text-black focus:outline-none focus:border-[#ff2d78] transition-colors"
        />
        <input
          data-testid="iq-contact-email"
          required
          type="email"
          value={contact.email}
          onChange={set("email")}
          placeholder="Email *"
          className="w-full border border-zinc-300 rounded-xl px-4 py-3.5 text-sm text-black focus:outline-none focus:border-[#ff2d78] transition-colors"
        />
        <input
          data-testid="iq-contact-country"
          value={contact.country}
          onChange={set("country")}
          placeholder="Country (optional)"
          className="w-full border border-zinc-300 rounded-xl px-4 py-3.5 text-sm text-black focus:outline-none focus:border-[#ff2d78] transition-colors"
        />
        <select
          data-testid="iq-contact-age-range"
          value={contact.age_range}
          onChange={set("age_range")}
          className="w-full border border-zinc-300 rounded-xl px-4 py-3.5 text-sm text-black focus:outline-none focus:border-[#ff2d78] transition-colors appearance-none cursor-pointer"
        >
          <option value="">Age range (optional)</option>
          {AGE_RANGE_OPTIONS.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      <div className="space-y-3 mt-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            data-testid="iq-consent-updates"
            type="checkbox"
            checked={contact.consent_updates}
            onChange={toggle("consent_updates")}
            className="mt-0.5 w-4 h-4 accent-[#ff2d78]"
          />
          <span className="font-body text-sm text-zinc-600">I'd like to receive WearSpray IQ news and development updates.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            data-testid="iq-consent-research"
            type="checkbox"
            checked={contact.consent_research}
            onChange={toggle("consent_research")}
            className="mt-0.5 w-4 h-4 accent-[#ff2d78]"
          />
          <span className="font-body text-sm text-zinc-600">I'm open to being contacted about potential research or product evaluation opportunities.</span>
        </label>
      </div>

      {/* Honeypot — hidden from real visitors, catches simple bots */}
      <input
        type="text"
        name="website"
        value={contact.website}
        onChange={set("website")}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute opacity-0 h-0 w-0 pointer-events-none"
      />

      {error && <p data-testid="iq-contact-error" className="font-body text-sm text-red-600 mt-4">{error}</p>}

      <p className="font-body text-[12px] text-zinc-400 mt-5 leading-relaxed">
        Submitting this form does not guarantee selection or early access. By continuing you agree
        to our <Link to="/privacy" className="underline hover:text-[#ff2d78]" target="_blank">Privacy Policy</Link>.
      </p>
    </div>
  );
}
